import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { stepLogApi } from "api/TenantSlug/StepLog/api";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import GetStepLogIntegrationsRequest = App.Http.Requests.StepLog.GetStepLogIntegrationsRequest;
import GetStepLogIncomingPostbacksRequest = App.Http.Requests.StepLog.GetStepLogIncomingPostbacksRequest;
import GetStepLogOutgoingPostbacksRequest = App.Http.Requests.StepLog.GetStepLogOutgoingPostbacksRequest;
import GetStepLogStructuresRequest = App.Http.Requests.StepLog.GetStepLogStructuresRequest;
import GetStepLogIncomingPostbacksResponse = App.Http.Responses.StepLog.GetStepLogIncomingPostbacksResponse;
import GetStepLogIncomingPostbackResponse = App.Http.Responses.StepLog.GetStepLogIncomingPostbackResponse;
import GetStepLogIntegrationsResponse = App.Http.Responses.StepLog.GetStepLogIntegrationsResponse;
import GetStepLogIntegrationResponse = App.Http.Responses.StepLog.GetStepLogIntegrationResponse;
import GetStepLogOutgoingPostbacksResponse = App.Http.Responses.StepLog.GetStepLogOutgoingPostbacksResponse;
import GetStepLogOutgoingPostbackResponse = App.Http.Responses.StepLog.GetStepLogOutgoingPostbackResponse;
import GetStepLogStructuresResponse = App.Http.Responses.StepLog.GetStepLogStructuresResponse;
import GetStepLogStructureResponse = App.Http.Responses.StepLog.GetStepLogStructureResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;

// Integration step logs

export const useGetIntegrationStepLogsQuery = (request: GetStepLogIntegrationsRequest, options = {}) => {
    return useQueryApi<GetStepLogIntegrationsResponse>({
        queryKey: ["getIntegrationStepLogs", request],
        queryFn: () => stepLogApi.listIntegrationStepLogs(request),
        ...options,
    });
};

export const useGetIntegrationStepLogQuery = (
    logId: string,
    request: GetStepLogIntegrationsRequest = {},
    options = {}
) => {
    return useQueryApi<GetStepLogIntegrationResponse>({
        queryKey: ["getIntegrationStepLog", logId, request],
        queryFn: () => stepLogApi.getIntegrationStepLog(logId, request),
        ...options,
    });
};

export const useExportIntegrationStepLogsMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportIntegrationStepLogs"],
        mutationFn: (request) => stepLogApi.exportIntegrationStepLogs(request),
        ...options,
    });
};

// Incoming postback step logs

export const useGetIncomingPostbackStepLogsQuery = (request: GetStepLogIncomingPostbacksRequest, options = {}) => {
    return useQueryApi<GetStepLogIncomingPostbacksResponse>({
        queryKey: ["getIncomingPostbackStepLogs", request],
        queryFn: () => stepLogApi.listIncomingPostbackStepLogs(request),
        ...options,
    });
};

export const useGetIncomingPostbackStepLogQuery = (
    logId: string,
    request: GetStepLogIncomingPostbacksRequest = {},
    options = {}
) => {
    return useQueryApi<GetStepLogIncomingPostbackResponse>({
        queryKey: ["getIncomingPostbackStepLog", logId, request],
        queryFn: () => stepLogApi.getIncomingPostbackStepLog(logId, request),
        ...options,
    });
};

export const useExportIncomingPostbackStepLogsMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportIncomingPostbackStepLogs"],
        mutationFn: (request) => stepLogApi.exportIncomingPostbackStepLogs(request),
        ...options,
    });
};

// Outgoing postback step logs

export const useGetOutgoingPostbackStepLogsQuery = (request: GetStepLogOutgoingPostbacksRequest, options = {}) => {
    return useQueryApi<GetStepLogOutgoingPostbacksResponse>({
        queryKey: ["getOutgoingPostbackStepLogs", request],
        queryFn: () => stepLogApi.listOutgoingPostbackStepLogs(request),
        ...options,
    });
};

export const useGetOutgoingPostbackStepLogQuery = (
    logId: string,
    request: GetStepLogOutgoingPostbacksRequest = {},
    options = {}
) => {
    return useQueryApi<GetStepLogOutgoingPostbackResponse>({
        queryKey: ["getOutgoingPostbackStepLog", logId, request],
        queryFn: () => stepLogApi.getOutgoingPostbackStepLog(logId, request),
        ...options,
    });
};

export const useExportOutgoingPostbackStepLogsMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportOutgoingPostbackStepLogs"],
        mutationFn: (request) => stepLogApi.exportOutgoingPostbackStepLogs(request),
        ...options,
    });
};

// Structure step logs

export const useGetStructureStepLogsQuery = (request: GetStepLogStructuresRequest, options = {}) => {
    return useQueryApi<GetStepLogStructuresResponse>({
        queryKey: ["getStructureStepLogs", request],
        queryFn: () => stepLogApi.listStructureStepLogs(request),
        ...options,
    });
};

export const useGetStructureStepLogQuery = (logId: string, request: GetStepLogStructuresRequest = {}, options = {}) => {
    return useQueryApi<GetStepLogStructureResponse>({
        queryKey: ["getStructureStepLog", logId, request],
        queryFn: () => stepLogApi.getStructureStepLog(logId, request),
        ...options,
    });
};

export const useExportStructureStepLogsMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportStructureStepLogs"],
        mutationFn: (request) => stepLogApi.exportStructureStepLogs(request),
        ...options,
    });
};
